import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount?.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    if (this.refs.cartBubbleCount) {
      this.refs.cartBubbleCount.textContent = value > 0 && value < 100 ? String(value) : '';
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    
    // Forzar actualización al hacer clic en cualquier parte por si hay cambios rápidos
    document.addEventListener('click', () => {
      setTimeout(() => this.ensureCartBubbleIsCorrect(), 400);
    });

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   */
  onCartUpdate = async (event) => {
    // Tomamos el conteo que manda el evento como base, pero renderCartBubble lo verificará con el servidor
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    let finalCount = itemCount;
    
    try {
      // Agregamos un número aleatorio (?t=...) para obligar a Shopify a darnos datos frescos del servidor
      const res = await fetch('/cart.js?t=' + Date.now());
      const cart = await res.json();
      
      // Filtramos dejando fuera logística o envío
      const realItems = cart.items.filter(item => {
        const type = (item.product_type || '').toLowerCase();
        const title = (item.product_title || '').toLowerCase();
        return type !== 'logistica' && type !== 'envio' && !title.includes('entrega') && !title.includes('horario');
      });
      
      finalCount = realItems.reduce((acc, item) => acc + item.quantity, 0);
    } catch (e) {
      console.error("Error recalculando en renderCartBubble:", e);
    }

    const isZero = finalCount === 0;

    // Control del número dentro de la burbuja
    if (this.refs.cartBubbleCount) {
      this.refs.cartBubbleCount.classList.toggle('hidden', isZero);
      if (isZero) {
        this.refs.cartBubbleCount.style.setProperty('display', 'none', 'important');
        this.refs.cartBubbleCount.textContent = '';
      } else {
        this.refs.cartBubbleCount.style.removeProperty('display');
        this.refs.cartBubbleCount.textContent = String(finalCount);
      }
    }

    // Control del círculo de la burbuja
    if (this.refs.cartBubble) {
      this.refs.cartBubble.classList.toggle('visually-hidden', isZero);
      if (isZero) {
        this.refs.cartBubble.style.setProperty('display', 'none', 'important');
        this.refs.cartBubble.style.setProperty('opacity', '0', 'important');
        this.refs.cartBubble.style.setProperty('visibility', 'hidden', 'important');
      } else {
        this.refs.cartBubble.style.removeProperty('display');
        this.refs.cartBubble.style.removeProperty('opacity');
        this.refs.cartBubble.style.removeProperty('visibility');
      }
    }

    this.currentCartCount = finalCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', finalCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || finalCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    if (!this.refs.cartBubbleCount) return;
    // Forzamos a que corra la validación real del servidor
    this.renderCartBubble(0, false, false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}